import { useEffect, useMemo, useState } from 'react';
import {
  Coffee, Utensils, Moon, Cookie, Flame, ChevronLeft, ChevronRight,
  TrendingDown, TrendingUp, Lightbulb, Sun, Sunset, Bed, Apple,
  Dumbbell, Zap, Leaf, Drumstick, Target, Droplet, Activity, Baby,
  AlertTriangle, CheckCircle2, HeartPulse, Droplets,
} from 'lucide-react';
import {
  weightLossPlan, weightLossPlanVeg, weightLossPlanNonVeg,
  weightGainPlan, weightGainPlanNonVeg, planTips, planMeta, computeTargets,
} from '../mock/mealPlans';
import { pregnancyPlan, pregnancyDailyFoods, pregnancyAvoid, pregnancyDisclaimer } from '../mock/pregnancyPlan';
import {
  diabeticPlan, diabeticWeekRotations, diabeticInclude, diabeticAvoid,
  diabeticLifestyle, diabeticDisclaimer,
} from '../mock/diabeticPlan';
import { useLang } from '../context/LanguageContext';

const iconByKey = {
  earlyMorning: Sun,
  breakfast: Coffee,
  midMorning: Apple,
  lunch: Utensils,
  eveningSnack: Sunset,
  snack: Cookie,
  preWorkout: Zap,
  postWorkout: Dumbbell,
  dinner: Moon,
  beforeBed: Bed,
};

const MealRow = ({ meal }) => {
  const Icon = iconByKey[meal.key] || Utensils;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#2a2b2f] last:border-b-0">
      <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-[#ff4e15]/10">
        <Icon size={16} className="text-[#ff4e15]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-white/50 uppercase tracking-wide flex items-center gap-2">
          <span>{meal.label}</span>
          {meal.time && <span className="text-white/30 normal-case tracking-normal">· {meal.time}</span>}
        </div>
        <div className="text-sm text-white mt-0.5 leading-snug">{meal.name}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs text-white/50">kcal</div>
        <div className="text-sm font-bold text-[#ff4e15]">{meal.kcal}</div>
      </div>
    </div>
  );
};

// ------- Pregnancy section (goal === 'pregnancy') -------
const PregnancySection = () => {
  const { t, tr } = useLang();
  const [expandedMonth, setExpandedMonth] = useState(1);

  return (
    <div data-testid="pregnancy-section">
      <div className="mb-5 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Baby size={16} className="text-[#ff4e15]" />
          {t('pregnancyDietPlan')}
        </div>
        <div className="text-xs text-white/60 mt-1">{t('pregnancyDietSubtitle')}</div>
      </div>

      {/* 9 months accordion */}
      <div className="space-y-2">
        {pregnancyPlan.map((m) => {
          const isOpen = expandedMonth === m.month;
          return (
            <div key={m.month} className="border border-[#2a2b2f] rounded-sm overflow-hidden">
              <button
                onClick={() => setExpandedMonth(isOpen ? null : m.month)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#141518] hover:bg-[#1c1d20] transition-colors"
                data-testid={`preg-month-${m.month}-btn`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#ff4e15]/15 border border-[#ff4e15]/40 flex items-center justify-center text-sm font-black text-[#ff4e15]">
                    {m.month}
                  </span>
                  <div className="text-left">
                    <div className="text-sm font-semibold">{t('monthLabel')} {m.month}</div>
                    <div className="text-xs text-white/50 mt-0.5">
                      {tr(m.weeks)} · {tr(m.focus)}
                    </div>
                  </div>
                </div>
                <ChevronRight
                  size={16}
                  className={`text-white/40 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                />
              </button>
              {isOpen && (
                <div className="px-4 py-4 bg-[#0d0e10] fade-in-up space-y-4">
                  <div className="flex items-start gap-2 text-xs text-white/70">
                    <HeartPulse size={14} className="text-[#ff4e15] mt-0.5 shrink-0" />
                    <span><span className="text-white/50 uppercase tracking-wide">{t('focusLabel')}:</span> {tr(m.focus)}</span>
                  </div>

                  {m.schedule && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50 mb-2">{t('dailySchedule')}</div>
                      <div className="divide-y divide-[#2a2b2f]">
                        {m.schedule.map((s, i) => (
                          <div key={i} className="flex items-start gap-3 py-2.5">
                            <div className="w-24 shrink-0 text-xs text-[#ff4e15] font-semibold uppercase tracking-wide">
                              {tr(s.time)}
                            </div>
                            <div className="flex-1 text-sm text-white leading-snug">{tr(s.food)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {m.tips && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50 mb-2">{t('keyRecommendations')}</div>
                      <ul className="space-y-1.5">
                        {m.tips.map((tp, i) => (
                          <li key={i} className="text-sm text-white/80 flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#ff4e15] mt-2 shrink-0" />
                            {tr(tp)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {m.include && (
                    <div className="p-3 bg-[#1f1210] border border-[#ff4e15]/30 rounded-sm text-xs text-white/80 flex items-start gap-2">
                      <CheckCircle2 size={14} className="text-[#ff4e15] mt-0.5 shrink-0" />
                      <span>{tr(m.include)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Daily foods throughout pregnancy */}
      <div className="mt-6 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <Utensils size={16} className="text-[#ff4e15]" />
          {tr(pregnancyDailyFoods.title)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {pregnancyDailyFoods.items.map((it, i) => (
            <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-[#2a2b2f] last:border-b-0">
              <div className="text-xs text-white/60 uppercase tracking-wide shrink-0">{tr(it.label)}</div>
              <div className="text-xs text-white text-right">{tr(it.value)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Foods to avoid */}
      <div className="mt-6 p-4 bg-[#141518] border border-red-500/30 rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <AlertTriangle size={16} className="text-red-400" />
          {tr(pregnancyAvoid.title)}
        </div>
        <ul className="space-y-1.5">
          {pregnancyAvoid.items.map((it, i) => (
            <li key={i} className="text-xs text-white/70 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-red-400 mt-1.5 shrink-0" />
              {tr(it)}
            </li>
          ))}
        </ul>
      </div>

      {/* Medical disclaimer */}
      <div className="mt-6 p-3 bg-[#1f1210] border border-[#ff4e15]/30 rounded-sm">
        <div className="flex items-start gap-2">
          <HeartPulse size={14} className="text-[#ff4e15] mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] text-[#ff4e15] uppercase tracking-wide font-bold">{t('pregnancyDisclaimerLabel')}</div>
            <p className="text-[11px] text-white/70 mt-1 leading-relaxed">{tr(pregnancyDisclaimer)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------- Diabetic section (goal === 'diabetic') -------
const DiabeticSection = () => {
  const { t, tr } = useLang();
  const [expandedDay, setExpandedDay] = useState(1);

  return (
    <div data-testid="diabetic-section">
      <div className="mb-5 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold">
          <Droplets size={16} className="text-[#ff4e15]" />
          {t('diabeticDietPlan')}
        </div>
        <div className="text-xs text-white/60 mt-1">{t('diabeticDietSubtitle')}</div>
      </div>

      {/* Days 1–7 accordion */}
      <div className="space-y-2">
        {diabeticPlan.map((d) => {
          const isOpen = expandedDay === d.day;
          return (
            <div key={d.day} className="border border-[#2a2b2f] rounded-sm overflow-hidden">
              <button
                onClick={() => setExpandedDay(isOpen ? null : d.day)}
                className="w-full flex items-center justify-between px-4 py-3 bg-[#141518] hover:bg-[#1c1d20] transition-colors"
                data-testid={`diabetic-day-${d.day}-btn`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#ff4e15]/15 border border-[#ff4e15]/40 flex items-center justify-center text-sm font-black text-[#ff4e15]">
                    {d.day}
                  </span>
                  <div className="text-left">
                    <div className="text-sm font-semibold">{t('day')} {d.day}</div>
                    <div className="text-xs text-white/50 mt-0.5">{d.meals.length} {t('meals')}</div>
                  </div>
                </div>
                <ChevronRight size={16} className={`text-white/40 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
              {isOpen && (
                <div className="px-4 py-2 bg-[#0d0e10] fade-in-up">
                  {d.meals.map((m, i) => {
                    const Icon = iconByKey[m.key] || Utensils;
                    return (
                      <div key={i} className="flex items-start gap-3 py-3 border-b border-[#2a2b2f] last:border-b-0">
                        <div className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-[#ff4e15]/10">
                          <Icon size={16} className="text-[#ff4e15]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white/50 uppercase tracking-wide">{tr(m.label)}</div>
                          <div className="text-sm text-white mt-0.5 leading-snug">{tr(m.name)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Weeks 2-4 rotation guidance */}
      <div className="mt-6 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <Lightbulb size={16} className="text-[#ff4e15]" />
          {t('weekRotations')}
        </div>
        <div className="space-y-4">
          {diabeticWeekRotations.map((wk, i) => (
            <div key={i}>
              <div className="text-xs text-[#ff4e15] font-semibold uppercase tracking-wide">{tr(wk.weekLabel)}</div>
              {wk.intro && <div className="text-xs text-white/70 mt-1">{tr(wk.intro)}</div>}
              <ul className="mt-2 space-y-1.5">
                {wk.bullets.map((b, j) => (
                  <li key={j} className="text-xs text-white/80 flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#ff4e15] mt-1.5 shrink-0" />
                    {tr(b)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Foods to include */}
      <div className="mt-6 p-4 bg-[#141518] border border-green-500/25 rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <CheckCircle2 size={16} className="text-green-400" />
          {tr(diabeticInclude.title)}
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {diabeticInclude.items.map((it, i) => (
            <li key={i} className="text-xs text-white/80 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-green-400 mt-1.5 shrink-0" />
              {tr(it)}
            </li>
          ))}
        </ul>
      </div>

      {/* Foods to avoid */}
      <div className="mt-4 p-4 bg-[#141518] border border-red-500/30 rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <AlertTriangle size={16} className="text-red-400" />
          {tr(diabeticAvoid.title)}
        </div>
        <ul className="space-y-1.5">
          {diabeticAvoid.items.map((it, i) => (
            <li key={i} className="text-xs text-white/70 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-red-400 mt-1.5 shrink-0" />
              {tr(it)}
            </li>
          ))}
        </ul>
      </div>

      {/* Lifestyle */}
      <div className="mt-4 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
        <div className="flex items-center gap-2 text-sm font-bold mb-3">
          <Activity size={16} className="text-[#ff4e15]" />
          {tr(diabeticLifestyle.title)}
        </div>
        <ul className="space-y-1.5">
          {diabeticLifestyle.items.map((it, i) => (
            <li key={i} className="text-xs text-white/80 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-[#ff4e15] mt-1.5 shrink-0" />
              {tr(it)}
            </li>
          ))}
        </ul>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-3 bg-[#1f1210] border border-[#ff4e15]/30 rounded-sm">
        <div className="flex items-start gap-2">
          <HeartPulse size={14} className="text-[#ff4e15] mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] text-[#ff4e15] uppercase tracking-wide font-bold">{t('pregnancyDisclaimerLabel')}</div>
            <p className="text-[11px] text-white/70 mt-1 leading-relaxed">{tr(diabeticDisclaimer)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ------- Main component -------
const MealPlan = ({ goal: defaultGoal, answers }) => {
  const [goal, setGoal] = useState(defaultGoal || 'loss');
  const [diet, setDiet] = useState('veg'); // 'veg' | 'nonveg'
  const [week, setWeek] = useState(1);
  const [expandedDay, setExpandedDay] = useState(1);
  const { t } = useLang();

  useEffect(() => {
    if (defaultGoal) setGoal(defaultGoal);
  }, [defaultGoal]);

  const isPregnancy = goal === 'pregnancy';
  const isDiabetic = goal === 'diabetic';

  // Fitness-plan (loss/gain) computations — only used when not pregnancy/diabetic
  const isGainNonVeg = goal === 'gain' && diet === 'nonveg';
  const isLossVeg = goal === 'loss' && diet === 'veg';
  const isLossNonVeg = goal === 'loss' && diet === 'nonveg';
  let plan;
  let key;
  if (goal === 'loss') {
    if (isLossVeg) { plan = weightLossPlanVeg; key = 'lossVeg'; }
    else if (isLossNonVeg) { plan = weightLossPlanNonVeg; key = 'lossNonVeg'; }
    else { plan = weightLossPlan; key = 'loss'; }
  } else if (goal === 'gain') {
    plan = isGainNonVeg ? weightGainPlanNonVeg : weightGainPlan;
    key = isGainNonVeg ? 'gainNonVeg' : 'gain';
  } else {
    // pregnancy / diabetic — fall back to loss for shared refs, but we won't render those sections
    plan = weightLossPlanVeg;
    key = 'lossVeg';
  }
  const tips = planTips[key];
  const meta = planMeta[key];

  const weekDays = useMemo(() => {
    const start = (week - 1) * 7;
    const end = Math.min(start + 7, 30);
    return plan.slice(start, end);
  }, [plan, week]);

  const totalWeeks = Math.ceil(30 / 7);
  const avgKcal = Math.round(plan.reduce((s, d) => s + d.total, 0) / plan.length);
  const targets = useMemo(() => computeTargets(answers, key), [answers, key]);

  const switchGoal = (g) => {
    setGoal(g);
    setWeek(1);
    setExpandedDay(1);
  };

  const switchDiet = (d) => {
    setDiet(d);
    setWeek(1);
    setExpandedDay(1);
  };

  const goalHeaderTitle = isPregnancy
    ? t('pregnancyDietPlan')
    : isDiabetic
      ? t('diabeticDietPlan')
      : meta.title;
  const goalHeaderSub = isPregnancy
    ? t('pregnancyDietSubtitle')
    : isDiabetic
      ? t('diabeticDietSubtitle')
      : `${t('avgLabel')} ${avgKcal} ${t('kcalPerDay')} · ${meta.subtitle}`;

  return (
    <div className="bg-[#1a1b1e] border border-[#2a2b2f] rounded-md p-6" data-testid="meal-plan-card">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold">{goalHeaderTitle}</h2>
          <div className="text-xs text-white/50 mt-0.5">
            {(isPregnancy || isDiabetic) ? (
              goalHeaderSub
            ) : (
              <>
                {t('avgLabel')} <span className="text-[#ff4e15] font-semibold">{avgKcal} {t('kcalPerDay')}</span> · {meta.subtitle}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {/* Goal toggle: loss / gain / pregnancy / diabetic */}
          <div className="inline-flex flex-wrap bg-[#0d0e10] border border-[#2a2b2f] rounded-sm p-1">
            <button
              onClick={() => switchGoal('loss')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                goal === 'loss' ? 'bg-[#ff4e15] text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="meal-loss-btn"
            >
              <TrendingDown size={13} /> {t('weightLoss')}
            </button>
            <button
              onClick={() => switchGoal('gain')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                goal === 'gain' ? 'bg-[#ff4e15] text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="meal-gain-btn"
            >
              <TrendingUp size={13} /> {t('weightGain')}
            </button>
            <button
              onClick={() => switchGoal('pregnancy')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                goal === 'pregnancy' ? 'bg-[#ff4e15] text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="meal-pregnancy-btn"
            >
              <Baby size={13} /> {t('pregnancy')}
            </button>
            <button
              onClick={() => switchGoal('diabetic')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                goal === 'diabetic' ? 'bg-[#ff4e15] text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="meal-diabetic-btn"
            >
              <Droplets size={13} /> {t('diabetic')}
            </button>
          </div>

          {/* Veg/non-veg only for loss and gain */}
          {(goal === 'gain' || goal === 'loss') && (
            <div className="inline-flex bg-[#0d0e10] border border-[#2a2b2f] rounded-sm p-1">
              <button
                onClick={() => switchDiet('veg')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                  diet === 'veg' ? 'bg-green-600 text-white' : 'text-white/60 hover:text-white'
                }`}
                data-testid="meal-veg-btn"
              >
                <Leaf size={13} /> {t('veg')}
              </button>
              <button
                onClick={() => switchDiet('nonveg')}
                className={`px-3 py-1.5 text-xs font-semibold rounded-sm flex items-center gap-1.5 transition-colors ${
                  diet === 'nonveg' ? 'bg-red-600 text-white' : 'text-white/60 hover:text-white'
                }`}
                data-testid="meal-nonveg-btn"
              >
                <Drumstick size={13} /> {t('nonVeg')}
              </button>
            </div>
          )}
        </div>
      </div>

      {isPregnancy ? (
        <PregnancySection />
      ) : isDiabetic ? (
        <DiabeticSection />
      ) : (
        <>
          {/* Personalized nutrition targets */}
          {targets && (
            <div className="mb-5 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
              <div className="flex items-center gap-2 text-sm font-bold mb-3">
                <Target size={16} className="text-[#ff4e15]" />
                {t('yourDailyTargets')}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-[#0d0e10] border border-[#2a2b2f] rounded-sm">
                  <Flame size={14} className="text-[#ff4e15] mx-auto mb-1" />
                  <div className="text-[10px] text-white/50 uppercase tracking-wide">{t('calories')}</div>
                  <div className="text-sm font-bold mt-0.5">{targets.calorieMin}–{targets.calorieMax}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">{t('kcalPerDayShort')}</div>
                </div>
                <div className="p-3 bg-[#0d0e10] border border-[#2a2b2f] rounded-sm">
                  <Dumbbell size={14} className="text-[#ff4e15] mx-auto mb-1" />
                  <div className="text-[10px] text-white/50 uppercase tracking-wide">{t('protein')}</div>
                  <div className="text-sm font-bold mt-0.5">{targets.proteinMin}–{targets.proteinMax}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">{t('gPerDay')}</div>
                </div>
                <div className="p-3 bg-[#0d0e10] border border-[#2a2b2f] rounded-sm">
                  <Droplet size={14} className="text-[#ff4e15] mx-auto mb-1" />
                  <div className="text-[10px] text-white/50 uppercase tracking-wide">{t('water')}</div>
                  <div className="text-sm font-bold mt-0.5">{targets.waterLitres}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">{t('litresPerDay')}</div>
                </div>
                <div className="p-3 bg-[#0d0e10] border border-[#2a2b2f] rounded-sm">
                  <Activity size={14} className="text-[#ff4e15] mx-auto mb-1" />
                  <div className="text-[10px] text-white/50 uppercase tracking-wide">{t('activity')}</div>
                  <div className="text-sm font-bold mt-0.5 capitalize">{targets.activityLabel}</div>
                  <div className="text-[10px] text-white/40 mt-0.5">TDEE ~{targets.tdee}</div>
                </div>
              </div>
              <div className="text-[10px] text-white/40 mt-3 text-center">
                {t('calculatedFrom')} {targets.age} y · {targets.heightCm} cm · {targets.weightKg} kg · {targets.gender}
              </div>
            </div>
          )}

          {/* Week selector */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setWeek((w) => Math.max(1, w - 1))}
              disabled={week === 1}
              className="p-2 border border-[#2a2b2f] rounded-sm hover:border-[#ff4e15] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label={t('prevWeek')}
            >
              <ChevronLeft size={16} />
            </button>
            <div className="flex gap-1.5 flex-wrap justify-center">
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map((w) => (
                <button
                  key={w}
                  onClick={() => { setWeek(w); setExpandedDay(null); }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors ${
                    week === w ? 'bg-[#ff4e15] text-white' : 'bg-[#0d0e10] border border-[#2a2b2f] text-white/70 hover:border-[#ff4e15]'
                  }`}
                >
                  {t('week')} {w}
                </button>
              ))}
            </div>
            <button
              onClick={() => setWeek((w) => Math.min(totalWeeks, w + 1))}
              disabled={week === totalWeeks}
              className="p-2 border border-[#2a2b2f] rounded-sm hover:border-[#ff4e15] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label={t('nextWeek')}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Days list */}
          <div className="space-y-2">
            {weekDays.map((d) => {
              const isOpen = expandedDay === d.day;
              return (
                <div key={d.day} className="border border-[#2a2b2f] rounded-sm overflow-hidden">
                  <button
                    onClick={() => setExpandedDay(isOpen ? null : d.day)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-[#141518] hover:bg-[#1c1d20] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-9 h-9 rounded-full bg-[#ff4e15]/15 border border-[#ff4e15]/40 flex items-center justify-center text-sm font-black text-[#ff4e15]">
                        {d.day}
                      </span>
                      <div className="text-left">
                        <div className="text-sm font-semibold">{t('day')} {d.day}</div>
                        <div className="text-xs text-white/50 flex items-center gap-1 mt-0.5">
                          <Flame size={11} className="text-[#ff4e15]" /> {d.total} kcal · {d.meals.length} {t('meals')}
                        </div>
                      </div>
                    </div>
                    <ChevronRight
                      size={16}
                      className={`text-white/40 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-4 py-2 bg-[#0d0e10] fade-in-up">
                      {d.meals.map((m) => (
                        <MealRow key={m.key} meal={m} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tips */}
          <div className="mt-6 p-4 bg-[#141518] border border-[#2a2b2f] rounded-sm">
            <div className="flex items-center gap-2 text-sm font-bold mb-3">
              <Lightbulb size={16} className="text-[#ff4e15]" />
              {goal === 'loss' ? t('weightLossJourney') : t('weightGainJourney')}
            </div>
            <ul className="space-y-1.5">
              {tips.map((tip) => (
                <li key={tip} className="text-xs text-white/70 flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#ff4e15] mt-1.5 shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {/* Workout guide image */}
          <div className="mt-6">
            <div className="flex items-center gap-2 text-sm font-bold mb-3">
              <Dumbbell size={16} className="text-[#ff4e15]" />
              {t('workoutGuide')}
            </div>
            <a
              href="/images/workout-guide.png"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-md overflow-hidden border border-[#2a2b2f] hover:border-[#ff4e15] transition-colors bg-black"
            >
              <img
                src="/images/workout-guide.png"
                alt={t('workoutGuideAlt')}
                className="w-full h-auto block"
                loading="lazy"
              />
            </a>
            <p className="mt-2 text-[10px] text-white/40 text-center">
              {t('workoutGuideCaption')}
            </p>
          </div>

          <p className="mt-4 text-[10px] text-white/40 leading-relaxed">
            {t('mealPlanDisclaimer')}
          </p>
        </>
      )}
    </div>
  );
};

export default MealPlan;
